import { axiosInstance } from './axiosInstance';

export type OperatorMessageCategory =
  | 'notice'
  | 'issue'
  | 'event'
  | 'reference'
  | 'other';

export const OPERATOR_MESSAGE_CATEGORIES: {
  value: OperatorMessageCategory;
  label: string;
}[] = [
  { value: 'notice', label: '공지' },
  { value: 'issue', label: '이슈' },
  { value: 'event', label: '이벤트' },
  { value: 'reference', label: '참고' },
  { value: 'other', label: '기타' },
];

export interface OperatorMessage {
  id: number;
  category: string;
  category_label: string;
  title: string;
  body: string;
  author_id?: number | null;
  author_username?: string | null;
  author_full_name?: string | null;
  created_at: string;
  updated_at: string;
  is_read: boolean;
}

export interface OperatorMessageReader {
  user_id: number;
  username?: string | null;
  full_name?: string | null;
  read_at?: string | null;
}

export interface OperatorMessageDetail extends OperatorMessage {
  readers: OperatorMessageReader[];
  unread_users: OperatorMessageReader[];
}

const BASE = '/api/operator-messages';

export const operatorMessageService = {
  async list(category?: string): Promise<{ items: OperatorMessage[]; total: number }> {
    const res = await axiosInstance.get<{ items: OperatorMessage[]; total: number }>(BASE, {
      params: category ? { category } : undefined,
    });
    return res.data;
  },

  async listUnread(): Promise<OperatorMessage[]> {
    const res = await axiosInstance.get<OperatorMessage[]>(`${BASE}/unread`);
    return Array.isArray(res.data) ? res.data : [];
  },

  async getDetail(id: number): Promise<OperatorMessageDetail> {
    const res = await axiosInstance.get<OperatorMessageDetail>(`${BASE}/${id}`);
    return res.data;
  },

  async create(payload: {
    category: OperatorMessageCategory;
    title: string;
    body: string;
  }): Promise<OperatorMessage> {
    const res = await axiosInstance.post<OperatorMessage>(BASE, payload);
    return res.data;
  },

  async update(
    id: number,
    payload: Partial<{ category: OperatorMessageCategory; title: string; body: string }>
  ): Promise<OperatorMessage> {
    const res = await axiosInstance.patch<OperatorMessage>(`${BASE}/${id}`, payload);
    return res.data;
  },

  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}/${id}`);
  },

  async removeBulk(ids: number[]): Promise<number> {
    const res = await axiosInstance.post<{ deleted?: number }>(`${BASE}/bulk-delete`, { ids });
    return Number(res.data?.deleted || 0);
  },

  async removeAll(): Promise<number> {
    const res = await axiosInstance.delete<{ deleted?: number }>(BASE);
    return Number(res.data?.deleted || 0);
  },

  async markRead(id: number): Promise<void> {
    await axiosInstance.post(`${BASE}/${id}/read`);
  },
};
